(ns quuppa.qmd.map
  (:require [clojure.string :as str]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [reitit.frontend.easy :as rfe]
            ["react" :as react]
            ;; This is v1.6.1.
            ;; v2 is completely rewritten and doesn't work the same way,
            ;; can't be upgraded.
            ;; TODO: Rewrite using something else.
            ["react-zoom-pan-pinch" :as pan]
            ["react-transition-group/TransitionGroup" :default TransitionGroup]
            ["react-transition-group/CSSTransition" :default CSSTransition]
            ["react-use-measure" :default useMeasure]
            ["react-swipeable" :as swipeable]
            [quuppa.ui.icons :refer [icon]]
            [quuppa.peweb.query :as q]
            [quuppa.qmd.state :as state]
            [quuppa.qmd.events :as e]
            [quuppa.qmd.subs :as s]
            [quuppa.qmd.map-utils :as map-utils]))

;; This is the scale level where QMD map will switch to the "biggest icons".
;; This is currently also used as initial zoom level.
(def +BIG-BREAKPOINT+ 2.5)

;; CSS/SVG transform string for CS coordinate transformation matrix.
;; This is used BEFORE entity x,y translate.
(def CS-TRANSFORM " scale(1 -1) ")

;; One an item is translated to CS positions, inside CS-TRANSFORM,
;; item CS can be flipped again to use normal direction.
;; This is used AFTER entity x,y translate.
(def LOCAL-TRANSFORM " scale(1 -1) ")

(defn entity-translate
  "Places an entity in x, y position
  amd changes the y coordinate axis to local direction."
  [x y]
  (str " translate(" x ", " y ") "
       LOCAL-TRANSFORM))

(def DISPLAY-SCALE 20)

(defn reverse-scaling [scale]
  (str " scale(" (/ 1 (* DISPLAY-SCALE scale)) ") "))

;; React context is used to pass some refs and other implementation details
;; from map-container to elements inside it.
;; Used by locator-circle here, and placement and orientation components by
;; those steps.
(def MapContext (react/createContext {}))

;; Special click event handling
;; ID takes care of only accepting inner click handler,
;; e.g. if clicking on locator circle to select locator,
;; ignore the container handler where locator selection is reset.
;; Also ignore clicks, if there is more than 15px of movement
;; between down/up events, to ignore up events after panning the
;; map.

(defn click-handler-up
  "Trigger f (click handler) if the mouse movement
  between down and up events was less than 15 px."
  [id f]
  (fn [_svg-ref click-state e]
    (when (= id (:down @click-state))
      (when (< (:movement @click-state) 15)
        (f e))
      (swap! click-state assoc :down nil))))

(defn click-handler-move [id]
  (fn [_svg-ref click-state e]
    (when (= id (:down @click-state))
      (swap! click-state update :movement +
             (js/Math.abs (.-movementX e))
             (js/Math.abs (.-movementY e))))))

(defn click-handler-down [id]
  (fn [_svg-ref click-state _e]
    (when (or (nil? (:down @click-state))
              ;; For some reason, without this,
              ;; after closing locator details, clicking on locator circle the first time
              ;; would ignored.
              (and (= :container (:down @click-state))
                   (= :locator id)))
      (reset! click-state {:down id
                           :movement 0}))))

(defn get-locator-image-position
  "Returns coordinates for locator inside the transform element coordinate space, in screen pixels,
  not svg coordinates."
  [cs current-scale container-bounds locator]
  ;; Svg-el .getCTM could be used to transform CS coordinates to element coordinate space.
  ;; But we don't necessarily have svg-el accessible here, we probably want to calculate
  ;; initial pos before first render using this fn.
  ;; And we'd still need to consider centering the location in the view etc., so current impl
  ;; is reasonable.
  (let [{:keys [x y]} (:converted-location locator)
        ;; Convert locator x,y to SVG coordinates using "zoom-scale" and
        ;; current view scale.
        ;; To center on the screen, add half of SVG element screen dimensions.
        ;; Automatically zoom closer to the locator, but don't zoom out if already
        ;; zoomed in.
        new-scale (max current-scale +BIG-BREAKPOINT+)
        bb (map-utils/extend-bounding-box (map-utils/get-svg-bounding-box cs))
        ;; SVG coordinates are multiplied by 20 to get screen coordinates.
        new-x (+ (* new-scale DISPLAY-SCALE (+ (:x bb)
                                               (- x)))
                 (/ (:width container-bounds) 2))
        new-y (+ (* new-scale DISPLAY-SCALE (- (+ (:y bb)
                                                  (:height bb)
                                                  (- y))))
                 (/ (:height container-bounds) 2))]
    {:x new-x
     :y new-y
     :scale new-scale}))

(rf/reg-event-fx ::zoom-to-locator
  (fn [{:keys [db]} [_ obj container-el locator animate?]]
    (when locator
      (let [rect (.getBoundingClientRect container-el)
            bounds {:width (.-width rect)
                    :height (.-height rect)}
            {:keys [token cs-id]} (-> db :quuppa.routing/match :parameters :path)
            cs (get-in (q/get-result db e/project-query {:token token}) [:result :coordinate-systems cs-id])
            {:keys [x y scale]} (get-locator-image-position cs (.-scale obj) bounds locator)
            ;; Animation makes the transition unrealiable.
            ;; Animate? is set to false when triggering zoom from list selection.
            ;; Still enabled when selecting locator on the map.
            anim-time (if animate? 250 0)]
        ;; For some reason need to wait for one frame or such
        ;; before changing transform or it doesn't work.
        (js/setTimeout (fn []
                         (.setTransform obj x y scale anim-time "linear"))
                       0)
        nil))))

(rf/reg-event-fx ::clear-selection
  (fn [_ _]
    {:fx [[:update-query [dissoc :selected-locator]]]}))

(rf/reg-event-fx ::select-locator
  (fn [{:keys [db]} [_ obj container-el locator-name]]
    (if locator-name
      (let [{:keys [cs-id token]} (-> db :quuppa.routing/match :parameters :path)
            cs (get-in (q/get-result db e/project-query {:token token}) [:result :coordinate-systems cs-id])
            locator (s/locator-by-name cs locator-name)]
        {:fx [[:update-query [assoc :selected-locator locator-name]]
              [:dispatch [::zoom-to-locator obj container-el locator true]]]})
      {:fx [[:update-query [dissoc :selected-locator]]]})))

(rf/reg-sub ::selected-locator?
  :<- [:nav/match :parameters :query :selected-locator]
  (fn [selected [_ locator-name]]
    (boolean (= selected locator-name))))

(defn locator-size [scale]
  (let [;; Reverse the scaling from zoom/pan component to keep the size constant
        size (/ 1 scale)]
    ;; Add scaling using zoom breakpoints
    (cond
      ; (> scale 3) size
      (>= scale +BIG-BREAKPOINT+) size
      (>= scale 0.5) (* 0.5 size)
      :else (* 0.25 size))))

(defn locator-circle [{:keys [locator disabled class]}]
  (let [{:keys [x y]} (:converted-location locator)
        ctx (react/useContext MapContext)
        scale (.-scale (:obj ctx))
        size (locator-size scale)
        text? (> scale 1.5)]
    [:g.qmd-map-locator
     {:transform (entity-translate x y)

      :on-mouse-down (when-not disabled
                       (partial (click-handler-down :locator) (:svg-ref ctx) (:click-state ctx)))
      :on-mouse-move (when-not disabled
                       (partial (click-handler-move :locator) (:svg-ref ctx) (:click-state ctx)))
      :on-mouse-up (when-not disabled
                     (partial (click-handler-up :locator (fn [_e] (rf/dispatch [::select-locator (:obj ctx) (.-current (:container-ref ctx)) (:name locator)])))
                              (:svg-ref ctx) (:click-state ctx)))
      :class [class
              (when disabled
                "qmd-map-locator--dim")
              (when @(rf/subscribe [::selected-locator? (:name locator)])
                "selected")]}
     [:circle.touch-area
      {:cx 0
       :cy 0
       :r (* 2 size)}]
     (if (state/ready? locator)
       [icon :check-circle-fill.text-light
        {:x (* -1 size)
         :y (* -1 size)
         :width (* 2 size)
         :height (* 2 size)}]
       [icon :exclamation-circle-fill.text-primary
        {:x (* -1 size)
         :y (* -1 size)
         :width (* 2 size)
         :height (* 2 size)}])
     (when text?
       [:text
        {:text-anchor "middle"
         :y (* 2 size)
         ;; TODO: Consider reversing scaling transformation first,
         ;; then can set normal size here?
         :style {:font-size (str size "px")}}
        (:name locator)])]))

(defn client-xy [e]
  [(or (.-clientX e)
       (some-> e .-touches (aget 0) .-clientX)
       (some-> e .-changedTouches (aget 0) .-clientX))
   (or (.-clientY e)
       (some-> e .-touches (aget 0) .-clientY)
       (some-> e .-changedTouches (aget 0) .-clientY))])

(defn map-toolbar [obj]
  [:div.qmd-map-toolbar.qmd-map-toolbar--top.p-1.d-none.d-md-block
   {:on-click (fn [e]
                (.stopPropagation e))}
   [:button.btn.btn-primary
    {:type "button"
     :on-click (.-zoomOut obj)}
    "-"]
   [:button.btn.btn-primary.ml-1
    {:type "button"
     :on-click (.-zoomIn obj)}
    "+"]])

(defn bottom-toolbar [{:keys [token cs]}]
  (let [{:keys [list-view selected-locator]} @(rf/subscribe [:nav/query-params])
        locator @(rf/subscribe [::s/locator-by-name token (:id cs) selected-locator])
        path-params {:token        token
                     :cs-id        (:id cs)
                     :locator-name (:name locator)}
        touch-handlers (js->clj (swipeable/useSwipeable
                                  #js {:onSwipedUp (fn [_e]
                                                     (if selected-locator
                                                       (rf/dispatch
                                                         [:nav/push-state
                                                          :locator-view
                                                          path-params
                                                          {:list-view        false
                                                           :selected-locator selected-locator}])
                                                       (rf/dispatch [:nav/update-query assoc :list-view (not list-view)])))})
                                :keywordize-keys true)]
    ;; TransitionGroup will handle enter and exit for the element.
    ;; Locator ID is used as React key for the children, so when selection
    ;; changes, the old element is removed and new created.
    [:> TransitionGroup
     {:appear true}
     (when selected-locator
       [:> CSSTransition
        {:key selected-locator
         :timeout 250
         :classNames "qmd-map-locator-toolbar"}
        [:div.qmd-map-locator-toolbar
         (merge {:on-click (fn [e]
                             (.stopPropagation e))
                 :on-mouse-up (fn [e]
                                (.stopPropagation e))}
                touch-handlers)
         [:a.d-flex.flex-row.p-3.align-items-center.text-decoration-none
          {:title "Locator status"
           :href (rfe/href
                   :locator-view
                   {:token token
                    :cs-id (:id cs)
                    :locator-name (:name locator)}
                   {:list-view false
                    :selected-locator selected-locator})}
          [:div.mr-3.d-flex.align-items-center
           (if (state/ready? locator)
             [icon :check-circle-fill.icon-md]
             [icon :exclamation-circle-fill.text-primary.icon-md])]
          [:div.d-flex.flex-column.flex-grow-1
           [:h5.fw-medium.mb-0 (:name locator)]
           [:div (state/qmd-status-text locator true)]]
          [:div.d-flex.align-items-center
           ;; Opens the wizard for the current stage.
           (when-not (state/ready? locator)
             (let [current-state (state/current-state locator)
                   {:keys [start-icon start-title]} (get state/states current-state)]
               [:button.icon-btn
                {:on-click (fn [e]
                             ;; Don't go to locator details (parent click handler)
                             (.preventDefault e)
                             (.stopPropagation e)
                             (rf/dispatch [:nav/update-query assoc :step (name current-state)]))}
                (icon start-icon {:title start-title})]))]]]])]))

(defn background-image [{:keys [token cs]} bg]
  (let [rect (:rect bg)
        ;; Hide image if loading it results in an error.
        ;; This component is using background-image id as react-key,
        ;; so this component is replaced if selected cs changes.
        [hide set-hide] (react/useState false)]
    (when-not hide
      [:g.qmd-map-background
       ;; Rotate the image, in coordinate system space, BEFORE translating to proper position.
       {:transform (str
                     " rotate(" (- (:rotation bg 0)) ") "
                     CS-TRANSFORM
                     ;; Need to include height in Y translation... as Y axis is reversed.
                     (entity-translate (:x rect) (+ (:height rect) (:y rect))))}
       [:image
        {:width (:width rect)
         :height (:height rect)
         :href (str "/qmd/" token "/background-images/" (:id cs) "/" (:id bg))
         :on-error (fn [_e]
                     ;; Event doesn't really give any information about possible
                     ;; HTTP error response, no way around that.
                     (set-hide true))}]
       [:rect
        {:x 0
         :y 0
         :width (:width rect)
         :height (:height rect)}]])))

(defn tracking-areas [{:keys [cs]}]
  [:g
   (for [[id {:keys [polygon]}] (:tracking-areas cs)
         :let [color (:color polygon)
               color (if (= "#0000FF" color)
                       ;; $ui-ultradark-grey
                       "#303030"
                       color)]]
     ;; TODO: Not sure about coordinates here?
     [:polygon
      {:key id
       :points (str/join " " (map (fn [[x y]] (str x "," y)) (:points polygon)))
       :style {:fill color}}])])

(defn get-svg-xy
  "Given svg element, dx/dy to adjust event coordinates, and a Mouse or Touch event,
  find coordinates for the event in SVG coordinate space.

  Dx/dy can be used to adjust touch position relative to the element anchor."
  [svg-ref [dx dy] [sx sy]]
  (let [^js svg-el (.-current svg-ref)
        ;; Matrix transformation combination:
        ;; To first apply A and then B matrix, calculate matrix: BA
        ;; so note that multiplication is done in reverse order.

        ;; Container screen position
        container-el (.-parentElement (.-parentElement (.-parentElement svg-el)))
        rect (.getBoundingClientRect container-el)
        container-translate (.translate (js/DOMMatrix.) (.-x rect) (.-y rect))

        sp (js/DOMPoint. (+ sx dx) (+ sy dy))

        ;; Get svg transform coordinate, without scaling and panning.
        ;; On Chrome we can get the pure SVG transform - with .getCTM.
        ;; On Chrome .getScreenCTM would also include scaling and panning.
        ;; On Firefox, .getCTM isn't available but .getScreenCTM is.

        ;; getScreenCTM also already translates the coordinates to svg element position,
        ;; so we need to reverse this, so we can later redo this later after pan&scaling.
        ;; On Chrome getScreenCTM would also contain scaling and panning, so we HOPE
        ;; getCTM is always available in the cases where getScreenCTM would include these.
        svg-transform (or (.getCTM svg-el)
                          (.multiply (.inverse container-translate) (.getScreenCTM svg-el)))

        ;; Get scaling and panning transform matrix
        transform-el (.-parentElement svg-el)
        transform-styles (.getComputedStyle js/window transform-el)
        ;; Matrix can be constructed from CSS transform value directly.
        map-view-transform (js/DOMMatrix. (.getPropertyValue transform-styles "transform"))

        ;; svg coordinates -> screen px
        ;; 1. svg transform, pixels inside the svg element
        ;; 2. map-view-transform, panning and scaling
        ;; 3. screen transform, container element place in the page
        ctm (.multiply container-translate (.multiply map-view-transform svg-transform))
        ;; screen px -> svg coordinates
        ictm (.inverse ctm)

        ;; Finally convert screen coordinate to svg space.
        p (.matrixTransform sp ictm)
        x (.-x p)
        ;; Y axis is reversed.
        y (- (.-y p))]
    [x y]))

(defn update-pattern-size [zoom-scale]
  ;; We need to revert the zoom scale so on zoom-in we will show bigger squares and on zoom-out smaller size.
  ;; If we use original zoom-scale it will be another way around.
  (let [reverted-zoom-scale (/ 1 zoom-scale)
        new-pattern-size {:grid {:width (/ 100 reverted-zoom-scale)
                                 :height (/ 100 reverted-zoom-scale)}}]
    (rf/dispatch [::e/update-new-pattern-size new-pattern-size])))

(defn qmd-map-grid [pattern-size obj]
  (let [grid-width (->> pattern-size
                              :grid
                              :width)
        grid-height (->> pattern-size
                         :grid
                         :height)
        zoom-scale (.-scale obj)]
  [:svg.qmd-grid
   [:pattern#grid
    {:width grid-width
     :height grid-height
     :patternUnits "userSpaceOnUse"}
    [:path
     {:d (str "M " (* 100 zoom-scale) " 0 L 0 0 0 " (* 100 zoom-scale))
      :fill "none"
      :stroke "lightgrey"
      :stroke-width "0.5"}]]
   [:rect {:width "100%" :height "100%" :fill "url(#grid)" :x 0 :y 0}]]))

(defn map-container [{:keys [cs token toolbar selected-locator
                             on-mouse-down on-mouse-move on-mouse-up
                             ref on-entered-ref]
                      :or {toolbar true}
                      :as props}
                     children]
  (let [;; Scale SVG from Quuppa coordinates to pixels.
        ;; Looks like Pan element zoom works better if we do some initial scaling to get SVG reasonable size.
        zoom-scale DISPLAY-SCALE

        ;; Easy way to have one atom for the component instance, without fn closure.
        [click-state _] (react/useState (atom nil))

        ;; Call the fn through track, so we only
        ;; call this if cs value changes.
        ;; Easier to use than reaction, and don't want to
        ;; wrap this in subscription because it is simpler to take
        ;; cs as a parameter here than from subscription.
        bb (map-utils/extend-bounding-box @(r/track map-utils/get-svg-bounding-box cs))

        ;; Measure the map container element.
        container-ref (react/useRef)
        [measure-container container-bounds refresh-measurement] (useMeasure)
        update-container-ref (react/useCallback (fn [el]
                                                  (measure-container el)
                                                  (set! (.-current container-ref) el)
                                                  (when (:container-ref props)
                                                    (set! (.-current (:container-ref props)) el))))
        svg-ref (react/useRef)
        ;; Fit the map image to be 75% of container size.
        ;; Map image is zoom-scale (currently 20 pixels per coordinate unit/meter)
        initial-scale +BIG-BREAKPOINT+

        ;; If we have selected-locator query-param, use that for the initial
        ;; map position. If not, get the first locator from selected cs, using
        ;; same sort as the locator list.
        locator (or @(rf/subscribe [::s/locator-by-name token (:id cs) selected-locator])
                    @(rf/subscribe [::s/first-locator token (:id cs)]))

        ;; Initial-scale is the same as target scale of the fn, so that will always get
        ;; used for initial position.
        ;; Normally the locator icon or locator list on-click handler
        ;; will trigger zoom to locator event.
        locator-pos (when (and (pos? (.-width container-bounds))
                               cs
                               locator)
                      (get-locator-image-position cs initial-scale (js->clj container-bounds :keywordize-keys true) locator))
        pattern-size @(rf/subscribe [::s/pattern-size])]

    (update-pattern-size 1)

    ;; Set up refresh callback to parent ref object if provided.
    (when on-entered-ref
      (set! (.-current on-entered-ref) (fn [] (refresh-measurement))))

    ;; Don't render Pan element before we know CS bounding box etc, or initial centering doesn't work.
    ;; Also wait before we have wrapper element measured, because we can set initial scale only on first TransformWrapper render.
    [:div.qmd-map-wrapper
     {:ref update-container-ref}
     (when (and (pos? (.-width container-bounds))
                cs)
       [:> pan/TransformWrapper
        ;; No stored/restored position currently.
        ;; Only case where that would be useful, would be for dev time reloads.
        {:defaultScale (or (:scale locator-pos) initial-scale)
         :defaultPositionX (or (:x locator-pos) 0)
         :defaultPositionY (or (:y locator-pos) 0)
         ;; What are these used for?
         :enablePadding false
         :enablePanPadding false
         ;; minScale doesn't seem to work on pinch zoom without disabling this
         :scalePadding {:disabled true}
         :wheel {:step 20}
         :zoomIn {:step 200
                  :animation false
                  :animationType "linear"}
         :zoomOut {:step 300
                   :animation false
                   :animationType "linear"}
         :onZoomChange (fn [params]
                         (update-pattern-size (.-scale params)))
         :pan {:padding true
               :paddingSize 100
               :animationType "linear"
               :velocity false}
         ;; If need to store pos/zoom.
         ; :onZoomChange nil
         ; :onPanningStop nil
         :options {:minScale 0.1
                   :maxScale 25
                   ;; Allow moving the image without limitations
                   ;; If true/false, will always center content on initial
                   ;; render, which breaks default positions.
                   :limitToBounds true
                   :limitToWrapper true
                   :centerContent true}}
        (fn [obj]
          (r/create-element
            (.-Provider MapContext)
            #js {:value {:obj obj
                         :svg-ref svg-ref
                         :click-state click-state
                         :container-ref container-ref
                         ;; Note: now this is recalculated for every viewport move? Is this fast enough?
                         :viewport (when (.-current svg-ref)
                                     (let [[left top]
                                           (get-svg-xy svg-ref [0 0] [(.-left container-bounds)
                                                                      (.-top container-bounds)])

                                           [right bottom]
                                           (get-svg-xy svg-ref [0 0] [(.-right container-bounds)
                                                                      (.-bottom container-bounds)])]
                                       {:left left
                                        :top top
                                        :right right
                                        :bottom bottom}))}}
            (r/as-element
              [:div.qmd-map-container
               {:on-mouse-down (partial on-mouse-down svg-ref click-state)
                :on-mouse-move (partial on-mouse-move svg-ref click-state)
                :on-touch-move (partial on-mouse-move svg-ref click-state)
                :on-mouse-up (partial on-mouse-up svg-ref click-state)
                :on-touch-end (partial on-mouse-up svg-ref click-state)}

               [qmd-map-grid pattern-size obj]

               (when toolbar
                 [map-toolbar obj])
               (when toolbar
                 [:f> bottom-toolbar props])
               [:> pan/TransformComponent
                [:svg.qmd-map
                 ;; Viewbox is the start x y and width and height of the coordinate system.
                 {;; Y axis is reversed
                  :ref svg-ref
                  :viewBox (str (:x bb) " "
                                (+ (- (:height bb))
                                   (- (:y bb))) " "
                                (:width bb) " "
                                (:height bb))
                  :width (* (:width bb) zoom-scale)
                  :height (* (:height bb) zoom-scale)
                  :tab-index "-1"}
                 [:g
                  {:transform CS-TRANSFORM}
                  [tracking-areas props]]

                 (for [[id bg] (:background-images cs)]
                   ^{:key id}
                   [:f> background-image props bg])

                 children]]])))])]))

(defn map-view [{:keys [cs] :as x}]
  [:f> map-container
   (assoc x
          :on-mouse-down (click-handler-down :container)
          :on-mouse-move (click-handler-move :container)
          :on-mouse-up (click-handler-up :container (fn [_e] (rf/dispatch [::clear-selection]))))
   [:g
    {:transform CS-TRANSFORM}
    (for [[_ locator] (:locators cs)]
      ^{:key (:id locator)}
      [:f> locator-circle
       {:locator locator}])]])
